import apiClient from 'apiClient/index';
import { MatchesSortBy } from 'constants/enums/matches-sort-by.enum';
import { CLOSE_FAMILY_GROUP } from 'constants/matches';
import { CachePool } from 'helpers/CachePool/CachePool';
import { StorageCacheStore } from 'helpers/CachePool/StorageCacheStore';
import { DeduplicationPool } from 'helpers/DeduplicationPool/DeduplicationPool';
import {
  action,
  configure,
  makeAutoObservable,
  observable,
  runInAction,
} from 'mobx';
import {
  Match, MatchGroup, MatchGroups, PawfectMatchesData,
} from 'stores/types/Matches';

const MATCHES_TTL_SECONDS = 86400;
const PAWFECT_MATCHES_TTL_SECONDS = 86400;

configure({
  enforceActions: 'observed',
  isolateGlobalState: true,
  reactionRequiresObservable: true,
});

class MatchesStore {
  pawfectMatchesData: PawfectMatchesData | null = null;
  dnaMatchesData: MatchGroups | null = null;
  dnaMatchesList?: Match[] | null = null;
  pawfectMatchesList: Match[] | null = null;
  loadingPawfectMatches: boolean = false;
  loadingDnaMatches: boolean = false;

  deduplicationPool = new DeduplicationPool();
  cachePool = new CachePool(new StorageCacheStore('MatchesStore'));

  constructor() {
    makeAutoObservable(this, {
      pawfectMatchesData: observable,
      pawfectMatchesList: observable,
      dnaMatchesData: observable,
      dnaMatchesList: observable,
      loadingPawfectMatches: observable,
      loadingDnaMatches: observable,
      getDnaMatches: action,
      getPawfectMatches: action,
      favoriteMatch: action,
      unfavoriteMatch: action,
      updateMatchStarred: action,
    });
  }

  getDnaMatches = async (
    testId: string,
    q?: string,
    sortBy?: MatchesSortBy,
    starred?: boolean,
    currentPage?: number,
  ) => {
    const key = `matches-${testId}-${q}-${sortBy}-${starred}-${currentPage}`;

    await this.deduplicationPool.runTask(
      key,
      async () => {
        this.loadingDnaMatches = true;
        const matchesResponse = await this.cachePool.runTask(
          key,
          async () => {
            const res = await apiClient.matches
              .getMatches(testId, q, sortBy, starred, currentPage);
            return res?.data ?? null;
          },
          MATCHES_TTL_SECONDS,
        );

        runInAction(() => {
          if (matchesResponse) {
            this.dnaMatchesData = matchesResponse;
            if (matchesResponse.matchGroups) {
              this.dnaMatchesList = matchesResponse.matchGroups
                .find((group: MatchGroup) => group.name.key === CLOSE_FAMILY_GROUP)?.matches;
              const newDateTime = new Date().getTime() - (20 * 24 * 60 * 60 * 1000);
              this.dnaMatchesList?.forEach((dm:Match) => {
                const matchDateTime = new Date(dm.createdDate).getTime();
                // eslint-disable-next-line no-param-reassign
                dm.new = matchDateTime > newDateTime;
              });
            }
          }
          this.loadingDnaMatches = false;
        });
      },
    );
  };

  getPawfectMatches = async (
    testId: string,
    q?: string,
    starred?: boolean,
    page?: number,
    pageSize?: number,
  ) => {
    const key = `pawfectMatches-${testId}-${q}-${starred}-${page}-${pageSize}`;
    await this.deduplicationPool.runTask(
      key,
      async () => {
        this.loadingPawfectMatches = true;

        const matchesResponse = await this.cachePool.runTask(
          key,
          async () => {
            const res = await apiClient.matches.getPawfectMatches(
              testId,
              q,
              starred,
              page,
              pageSize,
            );
            return res?.data ?? null;
          },
          PAWFECT_MATCHES_TTL_SECONDS,
        );

        runInAction(() => {
          if (matchesResponse) {
            this.pawfectMatchesData = matchesResponse;
            this.pawfectMatchesList = matchesResponse.pawfectmatches;
          }
          this.loadingPawfectMatches = false;
        });
      },
    );
  };

  favoriteMatch = async (testId: string, matchTestId: string) => {
    const matchesResponse = await apiClient.matches.addFavoriteMatch(testId, matchTestId);
    if (matchesResponse) {
      this.updateMatchStarred(matchTestId, true);
    }
  };

  unfavoriteMatch = async (testId: string, matchTestId: string) => {
    const matchesResponse = await apiClient.matches.removeFavoriteMatch(testId, matchTestId);
    if (matchesResponse) {
      this.updateMatchStarred(matchTestId, false);
    }
  };

  updateMatchStarred = (matchTestId: string, starred: boolean) => {
    if (this.dnaMatchesList) {
      const dm = this.dnaMatchesList.find((m) => m.testGUID === matchTestId);
      if (dm) {
        dm.starred = starred;
      }
    }
    if (this.pawfectMatchesList) {
      const pm = this.pawfectMatchesList.find((m) => m.testGUID === matchTestId);
      if (pm) {
        pm.starred = starred;
      }
    }
  };
}

export default MatchesStore;
